import * as React from 'react';
import { InputAdornment, TextField, Typography, useTheme, Button, Dialog } from '@mui/material';
import Grid from '@mui/material/Grid2';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { useContext, useState } from 'react';
import { SiteContext } from '../../store/siteContext';
import { StorageContext } from '../../store/storageContext';
import SiteForm from './SiteForm';
import StorageForm from './StorageForm';

type Props = {
	searchTerm: string;
	setSearchTerm: (value: string) => void;
	selectedSiteId: number | null;
};

const Header: React.FC<Props> = ({ searchTerm, setSearchTerm, selectedSiteId }) => {
	const theme = useTheme();
	const { t } = useTranslation();
	const { addSite, addSiteLoading, getSites } = useContext(SiteContext);
	const { addStorage, addStorageLoading } = useContext(StorageContext);
	const [addSiteOpen, setAddSiteOpen] = useState(false);
	const [addStorageOpen, setAddStorageOpen] = useState(false);

	const handleAddSiteSubmit = (data: SiteFormDto) => {
		addSite(data, () => {
			setAddSiteOpen(false);
			getSites(true);
		});
	};

	const handleAddStorageSubmit = (data: StorageFormDto) => {
		addStorage(data, () => {
			setAddStorageOpen(false);
		});
	};

	return (
		<>
			<Grid
				container
				size={{ xs: 12 }}
				alignItems="stretch"
				sx={{
					backgroundColor: theme.palette.headerBackground,
					height: 50,
					borderBottom: theme.palette.border,
				}}
			>
				<Grid
					size={{ xs: 3 }}
					sx={{
						borderRight: theme.palette.border,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						p: 2,
					}}
				>
					<Typography variant="subtitle2" sx={() => ({ ...theme.typography.columnHeader })}>
						{t('storages:t.sites')}
					</Typography>
					<Button
						sx={{ minWidth: 'auto', p: 0, color: 'green', height: 24 }}
						onClick={() => setAddSiteOpen(true)}
					>
						<AddIcon />
					</Button>
				</Grid>
				<Grid
					size={{ xs: 9 }}
					sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}
				>
					<Typography variant="subtitle2" sx={{ ...theme.typography.columnHeader }}>
						{t('storages:t.storages')}
					</Typography>
					<Button
						sx={{ minWidth: 'auto', p: 0, color: 'green', height: 24 }}
						onClick={() => setAddStorageOpen(true)}
					>
						<AddIcon />
					</Button>
				</Grid>
			</Grid>

			<Grid container size={{ xs: 12 }} alignItems="stretch" sx={{ borderBottom: theme.palette.border }}>
				<Grid size={{ xs: 3 }} sx={{ borderRight: theme.palette.border, p: 1 }}>
					<TextField
						variant="outlined"
						size="small"
						placeholder={'Find site...'}
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						sx={{
							mt: 0.5,
							width: '100%',
							'& .MuiOutlinedInput-root': {
								'& fieldset': {
									borderColor: 'rgba(0, 0, 0, 0.23)',
								},
								'&:hover fieldset': {
									borderColor: 'rgba(0, 0, 0, 0.87)',
								},
							},
							'& .MuiInputBase-input': {
								...theme.typography.usersFontRegular,
								fontSize: '0.8rem',
								py: 0.5,
							},
						}}
						slotProps={{
							input: {
								endAdornment: (
									<InputAdornment position="end">
										<SearchIcon sx={{ fontSize: '1rem', color: 'green' }} />
									</InputAdornment>
								),
							},
						}}
					/>
				</Grid>
				<Grid size={{ xs: 9 }} sx={{ borderLeft: theme.palette.border, minHeight: '32px', display: 'flex' }}>
					<Grid size={{ xs: 6 }} sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
						<Typography variant="subtitle2" sx={{ ...theme.typography.columnHeader }}>
							{t('storages:t.storage')}
						</Typography>
					</Grid>
					<Grid size={{ xs: 3 }} sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
						<Typography
							variant="subtitle2"
							sx={{ ...theme.typography.columnHeader, color: theme.palette.primary.main }}
						>
							{t('storages:t.lastStocking')}
						</Typography>
					</Grid>
					<Grid size={{ xs: 3 }} sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
						<Typography
							variant="subtitle2"
							sx={{ ...theme.typography.columnHeader, color: theme.palette.primary.main }}
						>
							{t('storages:t.lastInventory')}
						</Typography>
					</Grid>
				</Grid>
			</Grid>

			<Dialog open={addSiteOpen} onClose={() => setAddSiteOpen(false)}>
				<SiteForm
					onSubmit={handleAddSiteSubmit}
					buttonText={t('common:t.add')}
					loading={addSiteLoading}
					cancelButton
					onCancel={() => setAddSiteOpen(false)}
				/>
			</Dialog>

			<Dialog open={addStorageOpen} onClose={() => setAddStorageOpen(false)}>
				<StorageForm
					onSubmit={handleAddStorageSubmit}
					defaultValues={{
						name: '',
						siteId: selectedSiteId ?? null,
						lastReportDate: null,
						lastInventoryDate: null,
					}}
					buttonText={t('common:t.add')}
					loading={addStorageLoading}
					cancelButton
					onCancel={() => setAddStorageOpen(false)}
				/>
			</Dialog>
		</>
	);
};

export default Header;
